import { useEffect, useState, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe, MapPin } from "lucide-react";
import api from "@/utils/api";

// Lazy load the heavy GlobalMap component
const GlobalMap = lazy(() => import("@/components/GlobalMap"));

const MapPage = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ country: "", category: "", type: "" });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.country) params.set("country", filters.country);
        if (filters.category) params.set("category", filters.category);
        if (filters.type) params.set("listing_type", filters.type);

        const [mapRes, catRes] = await Promise.all([
          api.get(`/listings/map?${params.toString()}`),
          api.get("/categories")
        ]);
        setListings(mapRes.data.listings);
        setCountries(mapRes.data.countries || []);
        setCategories(catRes.data);
      } catch (error) {
        console.error("Failed to fetch map data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters]);

  const handleListingClick = (listingId) => {
    navigate(`/item/${listingId}`);
  };

  return (
    <div className="min-h-screen pt-52" data-testid="map-page">
      {/* Filters Bar */}
      <div className="bg-white border-b sticky top-16 z-40">
        <div className="container mx-auto px-4 md:px-6 py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-emerald-600" />
              <h1 className="font-outfit text-xl font-bold">Global Marketplace</h1>
            </div>
            
            <div className="flex flex-wrap gap-3 ml-auto">
              <Select value={filters.country || "all"} onValueChange={(v) => setFilters({ ...filters, country: v === "all" ? "" : v })}>
                <SelectTrigger className="w-[180px]" data-testid="country-filter">
                  <SelectValue placeholder="All Countries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Countries</SelectItem>
                  {countries.map((country) => (
                    <SelectItem key={country} value={country}>{country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.category || "all"} onValueChange={(v) => setFilters({ ...filters, category: v === "all" ? "" : v })}>
                <SelectTrigger className="w-[160px]" data-testid="map-category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.type || "all"} onValueChange={(v) => setFilters({ ...filters, type: v === "all" ? "" : v })}>
                <SelectTrigger className="w-[140px]" data-testid="map-type-filter">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="auction">Auctions</SelectItem>
                  <SelectItem value="fixed">Fixed Price</SelectItem>
                </SelectContent>
              </Select>

              {(filters.country || filters.category || filters.type) && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setFilters({ country: "", category: "", type: "" })}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="relative" style={{ height: 'calc(100vh - 130px)' }}>
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
            <div className="text-center">
              <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-muted-foreground">Loading map...</p>
            </div>
          </div>
        ) : (
          <Suspense fallback={
            <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
              <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
            </div>
          }>
            <GlobalMap 
              listings={listings}
              onListingClick={handleListingClick}
              selectedCountry={filters.country}
              selectedCategory={filters.category}
              selectedType={filters.type}
              height="100%"
            />
          </Suspense>
        )}

        {/* No listings message */}
        {!loading && listings.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Card className="p-8 text-center bg-white/90 backdrop-blur-sm pointer-events-auto">
              <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold text-lg mb-2">No listings with locations</h3>
              <p className="text-muted-foreground mb-4">
                Listings will appear on the map once sellers add their location
              </p>
              <Button 
                onClick={() => navigate('/sell')} 
                className="rounded-full bg-emerald-500 hover:bg-emerald-600"
              >
                Create a Listing
              </Button>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapPage;
